<template>
  <MdlListItem
    :thumbnail="model?.thumbnail"
    :badge="action.direction === 'out' ? 'arrow_forward' : 'arrow_back'"
    :badge-classes="action.direction === 'out' ? 'mdl-color-text--primary' : 'mdl-color-text--accent'"
    :progress="progress && progress < 100 ? progress : undefined"
    :background="getStateColor(action.state)"
    :initials="action.typeName.replaceAll(/[a-z]+/g, '').substring(0, 2)"
    expandable
    :expanded="selected"
    :loading="loading"
    :modified="action.modified"
    body-inset
    @toggle="onToggle"
  >
    <template #title>
      <span class="inline-flex items-center gap-x-2">
        <span>{{ action.description }}</span>
        <ActionLabel v-if="user" type="primary" :label="user.fullname" icon="person" :identifier="user.id" />
        <ActionLabel
          v-if="model && model.type !== 'user' && model.label"
          type="primary"
          outlined
          :label="model.label"
          :icon="model.icon"
          :identifier="model.key"
        />
      </span>
    </template>

    <template #subtitle>{{ `${action.type}(${action.argumentsString})` }}</template>

    <template #actions>
      <User v-if="action.creator" :user="action.creator" right class="mr-4" />

      <div class="flex flex-col">
        <span
          class="mdl-label mdl-label--default justify-center text-uppercase"
          :class="getStateLabelClasses(action.state)"
        >
          {{ action.state }}
        </span>
        <Date time :value="action.createdAt" />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4 p-[16px]">
        <DefinitionList>
          <DefinitionItem :term="trans('ID')" class="font-mono font-bold">{{ action.ulid }}</DefinitionItem>
          <DefinitionItem :term="trans('Source')">
            {{ trans(action.direction === 'out' ? 'Munio' : action.integration.typeName) }}
          </DefinitionItem>
          <DefinitionItem v-if="action.progress.total" :term="trans('Progress')">
            {{
              `${action.progress.current} / ${action.progress.total} (${Math.ceil(
                (action.progress.current / action.progress.total) * 100,
              )}%)`
            }}
          </DefinitionItem>
          <DefinitionItem v-if="user" :term="trans('User')">
            <a :href="route('lms.admin.user', { user: user.id })" target="_blank">{{ user.fullname }}</a>
          </DefinitionItem>
          <DefinitionItem v-if="model" :term="trans('Model')" :select="false">
            {{ model.typeLabel }}
          </DefinitionItem>
        </DefinitionList>

        <Alert v-if="can('munio') && action.error" type="danger" class="whitespace-pre-wrap m-0">
          {{ action.error }}
        </Alert>

        <div v-if="!loading && !action.events?.length" class="alert alert-info m-0">{{ trans('No events') }}</div>
      </div>

      <MdlTable v-if="action.events?.length">
        <MdlTableHeader>
          <th>{{ trans('Event') }}</th>
          <th>{{ trans('Date') }}</th>
          <th />
        </MdlTableHeader>

        <MdlTableRow v-for="actionEvent of action.events" :key="actionEvent.ulid" :data-ulid="actionEvent.ulid">
          <template v-if="actionEvent.eventType === 'integration_action'">
            <td>
              <div class="flex items-center gap-2">
                <Icon name="settings_ethernet" v-tooltip="trans('Action')" />
                <div>
                  <div class="flex items-center gap-2">
                    <strong>{{ actionEvent.event.description }}</strong>
                    <ActionLabel
                      v-if="actionEvent.event.user"
                      :label="actionEvent.event.user.fullname"
                      icon="person"
                      :identifier="actionEvent.event.user.id"
                    />
                    <ActionLabel
                      v-if="
                        actionEvent.event.model &&
                        actionEvent.event.model.type !== 'user' &&
                        actionEvent.event.model.label
                      "
                      outlined
                      :label="actionEvent.event.model.label"
                      :icon="actionEvent.event.model.icon"
                      :identifier="actionEvent.event.model.key"
                    />
                  </div>

                  <div class="opaque">
                    {{ actionEvent.event.integration.typeName }}.{{
                      `${actionEvent.event.typeName}(${actionEvent.event.argumentsString})`
                    }}
                  </div>
                </div>
              </div>
            </td>
            <td>
              <Date :value="actionEvent.event.createdAt" time />
            </td>
            <td>
              <div class="flex justify-end items-center gap-x-2">
                <span class="mdl-label mdl-label--default text-uppercase" :class="getStateLabelClasses(action.state)">
                  {{ action.state }}
                </span>
              </div>
            </td>
          </template>

          <template v-else-if="actionEvent.eventType === 'integration_action_request'">
            <td>
              <div class="flex items-center gap-2">
                <Icon name="sync_alt" v-tooltip="trans('Request')" />
                <div class="flex items-baseline gap-2">
                  <strong class="text-uppercase">{{ actionEvent.event.method }}</strong>
                  <span>{{ actionEvent.event.url }}</span>
                  <span v-if="actionEvent.event.tag" class="mdl-label">
                    {{ actionEvent.event.tag }}
                  </span>
                </div>
              </div>
            </td>
            <td>
              <Date :value="actionEvent.event.createdAt" time />
            </td>
            <td>
              <div v-if="actionEvent.event.response" class="flex justify-end items-center gap-x-2">
                <MdlButton icon="preview" @click="previewRequest = actionEvent.event" />
                <span
                  class="mdl-label mdl-label-default"
                  :class="{
                    'mdl-label--success': actionEvent.event.response.isSuccess,
                    'mdl-label--danger': !actionEvent.event.response.isSuccess,
                  }"
                >
                  {{ actionEvent.event.response.status }}
                  {{ actionEvent.event.response.statusText }}
                </span>
              </div>

              <template v-else>
                <MdlSpinner small />
              </template>
            </td>
          </template>
        </MdlTableRow>

        <tfoot>
          <tr>
            <td colspan="5">
              <MdlPaginator
                v-if="action.eventsMeta"
                :loading="paginating"
                :value="action.eventsMeta"
                @change="$emit('paginate', $event)"
              />
            </td>
          </tr>
        </tfoot>
      </MdlTable>
    </template>

    <template v-if="action.canCancel || action.canRetry" #card-actions>
      <MdlButton
        v-if="action.canCancel"
        :disabled="action.state === 'cancelled'"
        :loading="cancelling"
        outlined
        primary
        @click.stop="onCancel"
      >
        {{ trans('Cancel') }}
      </MdlButton>

      <MdlButton
        v-if="action.canRetry"
        :disabled="action.state !== 'failed'"
        :loading="retrying"
        raised
        primary
        @click.stop="onRetry"
      >
        {{ trans('Retry') }}
      </MdlButton>
    </template>

    <RequestPreview v-if="previewRequest" :integration="action.integration.id" v-model="previewRequest" />
  </MdlListItem>
</template>

<script>
import { computed, ref } from 'vue'
import { trans } from '@/munio/i18n'
import { titleCase } from '@/munio/utils'
import { stateColor, stateIcon, stateLabelClasses } from './helpers'
import useStore from './store.js'
import Date from '@component/Date.vue'
import DefinitionList from '@component/DefinitionList.vue'
import DefinitionItem from '@component/DefinitionItem.vue'
import Icon from '@component/Icon.vue'
import MdlButton from '@component/mdl/Button.vue'
import MdlListItem from '@component/mdl/ListItem.vue'
import MdlPaginator from '@component/mdl/Paginator.vue'
import MdlTable from '@component/mdl/DataTable.vue'
import MdlTableHeader from '@component/mdl/DataTableHeader.vue'
import MdlTableRow from '@component/mdl/DataTableRow.vue'
import MdlSpinner from '@component/mdl/Spinner.vue'
import RequestPreview from './RequestPreview.vue'
import User from '@component/User.vue'
import tooltip from '@directive/tooltip.js'
import Alert from '@component/Alert.vue'
import ActionLabel from './ActionLabel.vue'

export default {
  name: 'IntegrationAction',
  components: {
    ActionLabel,
    Alert,
    Date,
    DefinitionItem,
    DefinitionList,
    Icon,
    MdlButton,
    MdlListItem,
    MdlPaginator,
    MdlTable,
    MdlTableHeader,
    MdlTableRow,
    MdlSpinner,
    RequestPreview,
    User,
  },

  directives: {
    tooltip,
  },

  props: {
    action: { type: Object, required: true },
    hideModel: { type: Boolean },
  },

  setup(props) {
    const store = useStore()
    const model = computed(() => {
      if (props.hideModel) {
        return undefined
      }

      return props.action.model
    })
    const selected = computed(() => {
      return props.action.ulid === store.actions.selected
    })

    const cancelling = ref(false)
    const retrying = ref(false)
    const previewRequest = ref(null)

    return {
      model,
      selected,
      cancelling,
      retrying,
      previewRequest,
      titleCase,
      route: window.Munio.route,
      user: computed(() => props.action.user),
      thumbnail: computed(() => model.value?.thumbnail),
      icon: computed(() => props.action.icon),
      subtitle: computed(() => {
        return model.value?.description ?? props.action.label
      }),
      loading: computed(() => {
        return props.action.ulid === store.actions.loading && selected.value
      }),
      paginating: computed(() => {
        return props.action.ulid === store.actions.pending
      }),
      progress: computed(() => {
        if (!props.action.progress?.total) {
          return undefined
        }

        return ((props.action.progress.current || 0) / props.action.progress.total) * 100
      }),

      onToggle: () => {
        store.toggleAction(props.action)
      },

      onPaginate(page) {
        this.$store.dispatch('loadAction', { action: this.action, page })
      },

      onCancel: async () => {
        if (!(await Munio.confirm())) {
          return
        }

        try {
          cancelling.value = true
          Munio.api.integration(props.action.integration.id).action(props.action.ulid).cancel()
        } catch (err) {
          console.error(err)
        } finally {
          cancelling.value = false
        }
      },

      onRetry: async () => {
        try {
          retrying.value = true
          Munio.api.integration(props.action.integration.id).action(props.action.ulid).retry()
        } catch (err) {
          console.error(err)
        } finally {
          retrying.value = false
        }
      },

      getStateIcon: (state) => stateIcon(state),

      getStateColor: (state) => stateColor(state),

      getStateLabelClasses: (state) => stateLabelClasses(state),
    }
  },

  methods: {
    trans,
    can: window.can,
  },
}
</script>
